/**
 * CountSessionStart — MADE-290
 *
 * PWA home-screen entry point: fetches (or creates) the newest active count
 * session and immediately redirects to /count/:sessionId. Staff tap this from
 * the installed app icon; no QR-code scanning or address-bar interaction needed.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';

const CountSessionStart: React.FC = () => {
    const navigate = useNavigate();
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;

        fetch('/api/madebb/count-sessions/active')
            .then(async (res) => {
                const data = await res.json();
                if (cancelled) return;
                if (data.success && data.session?.sessionId) {
                    navigate(`/count/${data.session.sessionId}`, { replace: true });
                } else {
                    setError(data.error || 'Could not start a count session.');
                }
            })
            .catch(() => {
                if (!cancelled) setError('Network error — check your connection.');
            });

        return () => { cancelled = true; };
    }, [navigate]);

    if (error) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Typography variant="h6" color="error" sx={{ mb: 1 }}>
                    Unable to Start Session
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {error}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Contact your supervisor if this persists.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
                Starting count session…
            </Typography>
        </Box>
    );
};

export default CountSessionStart;
